/*
 * Provides a service for interacting with the Microsoft Authentication Library. 
 * This is mostly lifted from the original Single Ticket View app. The only real difference
 * is that the config file has been changed to be stored in public so it can be changed depending on deployment
 */

import React, { useEffect, useState, useContext } from "react";
import * as msal from "msal";
import LoggingIn from "../pages/LoggingIn";
import { SSO_AUTHORITY, SSO_TENANCY, SSO_CLIENT_ID, SSO_REDIRECT_URL } from "../environmentVariables";

interface AuthContextData {
  loggedIn: boolean;
  loading: boolean;
  getAccessToken: () => Promise<string>;
  getLoggedInUsername: () => string | null;
  getLoggedInName: () => string | null;
}

const AuthContext = React.createContext<AuthContextData>({} as AuthContextData);

let initPromise: Promise<void>;
let msalApplication: msal.UserAgentApplication;
let authRequest = { scopes: [""] };

const coreInit = async () => {
  const msalConfig: msal.Configuration = {
    auth: {
      clientId: SSO_CLIENT_ID,
      authority: `${SSO_AUTHORITY}${SSO_TENANCY}`,
      redirectUri: SSO_REDIRECT_URL
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: true
    }
  };
  
  authRequest = {
    scopes: [`api://${SSO_CLIENT_ID}/ticket.portal`]
  };

  msalApplication = new msal.UserAgentApplication(msalConfig);
  msalApplication.handleRedirectCallback(() => {});
}

const initApplication = () => {
  if (!initPromise) {
    initPromise = coreInit();
  }
  return initPromise;
};

const getAccessToken = async () => {
    
  try {
    const accessTokenResponse = await msalApplication.acquireTokenSilent(
      authRequest
    );
    return accessTokenResponse.accessToken;
  } catch {
    msalApplication.acquireTokenRedirect(
      authRequest
    );
    return '';
  }
};

const getLoggedInUsername = () => {
  const user = msalApplication.getAccount();
  return user ? user.userName : null;
};

const getLoggedInName = () => {
    const user = msalApplication.getAccount();
    return user ? user.name : null;
}

const performLogin = () => msalApplication.loginRedirect(authRequest);

const AuthProvider: React.FC = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkLogin = async () => {
      setLoading(true);
      await initApplication();
      const user = msalApplication.getAccount();

      if (user) {
        try {
          const token = await getAccessToken();
          setLoggedIn(!!token);
        } catch {
          setLoggedIn(false);
        }
      } else {
        setLoggedIn(false);
      }
      setLoading(false);
    };

    checkLogin();
  }, []);

  useEffect(() => {
    if (!loading && !loggedIn) {
      performLogin();
    }
  }, [loading, loggedIn]);

  const data: AuthContextData = {
    getAccessToken,
    getLoggedInUsername,
    getLoggedInName,
    loading,
    loggedIn
  };

  if (!loggedIn || loading) {
    return <LoggingIn />;
  }

  if (window.self === window.top) {
    return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
  } else {
    return <></>;
  }
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
